import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";
import { showConfirmation } from "../helpers/confirmation_dialog";
import { loadFilePond } from "../models/filepond_loader";

// One coordinator avoids multiple prompts when a page contains several forms.
const forms = new Set();
let currentEntry;
let restoreHistoryDone = null;
let confirmationOpen = false;
const approvedModalHides = new WeakSet();

function rememberEntry() {
  currentEntry = { state: history.state, url: location.href };
}

function dirtyForms(container = document) {
  return [...forms].filter(form => container.contains(form.element) && form.isDirty());
}

async function confirmLeaving(container = document) {
  const dirty = dirtyForms(container);
  if (!dirty.length) return true;
  if (confirmationOpen) return false;
  confirmationOpen = true;
  try {
    const confirmed = await showConfirmation({
      title: dirty[0].titleValue,
      text: dirty[0].messageValue,
      confirmButtonText: "Discard",
      cancelButtonText: "Keep Editing",
    });
    if (!confirmed || !dirty.some(form => forms.has(form))) return false;
    for (const form of dirty) {
      if (!forms.has(form)) continue;
      await form.discard();
      form.allowNavigation = true;
    }
    return true;
  } finally {
    confirmationOpen = false;
  }
}

async function beforeVisit(event) {
  if (!dirtyForms().length) return;
  event.preventDefault();
  if (await confirmLeaving()) {
    Turbo.visit(event.detail.url, { action: event.detail.action || "advance" });
  }
}

async function nativeLinkClick(event) {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const link = event.target.closest("a[href]");
  if (!link || link.hasAttribute("download") || (link.target && link.target !== "_self") || link.dataset.turboMethod) return;
  const url = new URL(link.href, location.href);
  if (!["http:", "https:"].includes(url.protocol)) return;
  if (url.pathname === location.pathname && url.search === location.search && url.hash) return;
  if (url.origin === location.origin && link.closest("[data-turbo]")?.dataset.turbo !== "false") return;
  if (!dirtyForms().length) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (await confirmLeaving()) location.assign(url.href);
}

function beforeUnload(event) {
  if (!dirtyForms().length) return;
  event.preventDefault();
  event.returnValue = "";
}

async function popState(event) {
  if (restoreHistoryDone) {
    event.stopImmediatePropagation();
    const done = restoreHistoryDone;
    restoreHistoryDone = null;
    done();
    return;
  }
  if (!dirtyForms().length) return;

  // Restore the current entry before asking. Turbo must not replace the draft
  // while SweetAlert is open; replay the history traversal only after approval.
  event.stopImmediatePropagation();
  const destination = location.href;
  const from = currentEntry?.state?.turbo?.restorationIndex;
  const to = event.state?.turbo?.restorationIndex;
  const delta = Number.isInteger(from) && Number.isInteger(to) ? to - from : 0;
  if (delta) {
    await new Promise(resolve => {
      restoreHistoryDone = resolve;
      history.go(-delta);
    });
  } else if (currentEntry) {
    history.replaceState(currentEntry.state, "", currentEntry.url);
  }
  if (await confirmLeaving()) {
    if (delta) history.go(delta);
    else Turbo.visit(destination, { action: "replace" });
  }
}

async function beforeFrameRender(event) {
  if (!dirtyForms(event.target).length) return;
  event.preventDefault();
  if (await confirmLeaving(event.target)) event.detail.resume();
}

async function modalHide(event) {
  if (approvedModalHides.has(event.target)) {
    approvedModalHides.delete(event.target);
    for (const form of forms) {
      if (event.target.contains(form.element)) form.allowNavigation = false;
    }
    return;
  }
  if (!dirtyForms(event.target).length) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (await confirmLeaving(event.target)) {
    approvedModalHides.add(event.target);
    globalThis.bootstrap.Modal.getInstance(event.target)?.hide();
  }
}

function pageShow() {
  rememberEntry();
  for (const form of forms) form.allowNavigation = false;
}

const listeners = [
  [document, "turbo:before-visit", beforeVisit],
  [document, "click", nativeLinkClick, true],
  [document, "turbo:before-frame-render", beforeFrameRender],
  [document, "hide.bs.modal", modalHide, true],
  [document, "turbo:load", pageShow],
  [globalThis, "pageshow", pageShow],
  [globalThis, "beforeunload", beforeUnload],
  [globalThis, "popstate", popState, true],
];

export default class UnsavedChangesController extends Controller {
  static values = {
    title: { type: String, default: "Discard changes?" },
    message: { type: String, default: "You have unsaved changes. Leave without saving?" },
    cleanOnSubmit: { type: Boolean, default: true },
  };

  connect() {
    this.allowNavigation = false;
    this.submitting = false;
    this.initialState = this.element.dataset.unsavedChangesBaseline || this.serialize();
    this.element.dataset.unsavedChangesBaseline = this.initialState;
    this.onEdit = () => {
      this.allowNavigation = false;
      rememberEntry();
    };
    this.onSubmitStart = () => { this.submitting = true; };
    this.onSubmitEnd = event => {
      this.submitting = false;
      if (event.detail.success && this.cleanOnSubmitValue) this.markClean();
    };
    this.onClean = () => this.markClean();
    this.bindings = [
      ["input", this.onEdit], ["change", this.onEdit],
      ["turbo:submit-start", this.onSubmitStart],
      ["turbo:submit-end", this.onSubmitEnd],
      ["unsaved-changes:clean", this.onClean],
    ];
    for (const [name, handler] of this.bindings) this.element.addEventListener(name, handler);
    if (!forms.size) {
      rememberEntry();
      for (const [target, name, handler, capture] of listeners) target.addEventListener(name, handler, capture);
    }
    forms.add(this);
  }

  disconnect() {
    for (const [name, handler] of this.bindings) this.element.removeEventListener(name, handler);
    forms.delete(this);
    if (!forms.size) {
      for (const [target, name, handler, capture] of listeners) target.removeEventListener(name, handler, capture);
      restoreHistoryDone?.();
      restoreHistoryDone = null;
    }
  }

  markClean() {
    this.initialState = this.serialize();
    this.element.dataset.unsavedChangesBaseline = this.initialState;
    this.allowNavigation = false;
  }

  async discard() {
    const baseline = JSON.parse(this.initialState);
    const pondRoots = this.element.querySelectorAll(".filepond--root");
    if (pondRoots.length) {
      const FilePond = await loadFilePond();
      this.FilePond = FilePond;
      await Promise.all(Array.from(pondRoots).map(async root => {
        const pond = FilePond.find(root);
        if (!pond) return;
        // Discard newly selected files without deleting previously saved media.
        const drafts = pond.getFiles().filter(file =>
          file.origin === FilePond.FileOrigin.INPUT || file.origin === FilePond.FileOrigin.LIMBO
        );
        if (!drafts.length) return;

        // Discard was already confirmed. Don't open a second removal dialog.
        const beforeRemoveFile = pond.beforeRemoveFile;
        pond.beforeRemoveFile = null;
        try {
          // removeFiles(ids) returns an array of promises in this FilePond version.
          await Promise.all(drafts.map(file => pond.removeFile(file.id, { revert: false, remove: false })));
        } finally {
          pond.beforeRemoveFile = beforeRemoveFile;
        }
      }));
    }

    this.element.reset();
    const editors = this.element.querySelectorAll("[data-controller~='quill'], [contenteditable='true']:not(.ql-editor)");
    for (const [index, wrapper] of Array.from(editors).entries()) {
      const controller = this.application.getControllerForElementAndIdentifier(wrapper, "quill");
      const html = baseline.editors[index] || "";
      if (controller?.quill) {
        controller.clear();
        if (html) controller.quill.clipboard.dangerouslyPasteHTML(html);
      } else {
        (wrapper.querySelector(".ql-editor") || wrapper).innerHTML = html;
      }
    }
    // Let editor/upload listeners settle before Turbo captures the clean form.
    await new Promise(resolve => requestAnimationFrame(resolve));
    this.markClean();
  }

  isDirty() {
    return !this.allowNavigation && !this.submitting && this.serialize() !== this.initialState;
  }

  serialize() {
    const fields = Array.from(this.element.elements || []).filter(field =>
      field.name && !["hidden", "submit", "button", "reset"].includes(field.type) &&
      (field.type !== "file" || field.files?.length) &&
      !field.closest("[data-unsaved-changes-ignore], .filepond--root")
    ).map(field => {
      if (field.type === "file") {
        return [field.name, Array.from(field.files || []).map(file => [file.name, file.size, file.lastModified])];
      }
      if (["checkbox", "radio"].includes(field.type)) return [field.name, field.value, field.checked];
      if (field.multiple) return [field.name, Array.from(field.selectedOptions).map(option => option.value)];
      return [field.name, field.value];
    });
    const editors = Array.from(this.element.querySelectorAll("[data-controller~='quill'], [contenteditable='true']:not(.ql-editor)")).map(wrapper => {
      const editor = wrapper.querySelector(".ql-editor") || wrapper;
      return editor.textContent.trim() || editor.querySelector("img, video, iframe") ? editor.innerHTML.trim() : "";
    });
    // Removed preview nodes remain during FilePond's exit animation. They are
    // not draft files and must not become part of the new clean baseline.
    const attachments = Array.from(this.element.querySelectorAll(".filepond--root")).flatMap(root => {
      const pond = this.FilePond?.find(root);
      if (pond) return pond.getFiles().map(file => file.id);
      return Array.from(root.querySelectorAll(".filepond--item"))
        .map(item => item.id.replace(/^filepond--item-/, ""));
    });
    return JSON.stringify({ fields, editors, attachments });
  }
}
